import { useSelector } from 'react-redux';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useCacheCgdo } from 'app/shared/queries/useCacheCgdo';
import { computeDlHashIdForCgdo } from 'app/shared/utils/dlHashId';

// Components
import {
  U21SelectSearch,
  U21SelectSearchProps,
} from 'app/shared/u21-ui/components';
import { IconSearch } from '@u21/tabler-icons';

// Selectors
import {
  selectAgentOrgID,
  selectHasReadEventsPermission,
} from 'app/modules/session/selectors';

// Constants
import { INFINITE_SCROLL_LIMIT, OFFSET_DEFAULT } from 'app/shared/constants';

// Helpers
import { formatActionEventOptions } from 'app/modules/search/helpers';
import { ActionEventSearchOptions, MatchType } from 'app/modules/search/enum';
import { useQueryClient } from '@tanstack/react-query';
import { ActionEventSearchPayload } from 'app/modules/search/payload';
import {
  searchActionEvent,
  useActionEventSearch,
} from 'app/modules/actionEvents/queries/useActionEventSearch';
import { ACTION_EVENT_QUERY_KEYS } from 'app/modules/actionEvents/queries/keys';
import { ActionEventExternalId } from 'app/shared/types/eventExternalIds';

export type U21ActionEventSearchProps<TClearable extends boolean> =
  DistributiveOmit<
    U21SelectSearchProps<ActionEventExternalId, TClearable>,
    'options' | 'onSearch'
  >;

export const U21ActionEventSearch = <TClearable extends boolean>(
  props: U21ActionEventSearchProps<TClearable>,
) => {
  const { multi, value } = props;
  const queryClient = useQueryClient();

  const [newPayload, setNewPayload] = useState<ActionEventSearchPayload>();
  const [searchValue, setSearchValue] = useState<string>('');

  const hasReadEventsPermissions = useSelector(selectHasReadEventsPermission);

  const {
    actionEvents,
    query: { isFetching, isError: isV2Error },
    refreshActionEvents,
  } = useActionEventSearch(newPayload, true);

  const options = useMemo(
    () => formatActionEventOptions(actionEvents),
    [actionEvents],
  );

  const orgId = useSelector(selectAgentOrgID);
  const { mutate: cacheCgdo } = useCacheCgdo();

  const cachePrefetch = useCallback(
    (newValues: ActionEventExternalId[]) => {
      const prevSet = new Set(Array.isArray(value) ? value : []);
      newValues.forEach((i) => {
        if (prevSet.has(i)) return;
        cacheCgdo({
          object_type: 'action_event',
          hashed_id: computeDlHashIdForCgdo(orgId, String(i)),
        });
      });
    },
    [cacheCgdo, orgId, value],
  );

  // calledRef is used to prevent an infinite loop in case the API never returns with the missing value
  const calledRef = useRef(false);
  useEffect(() => {
    calledRef.current = false;
  }, [value]);

  useEffect(() => {
    if (!hasReadEventsPermissions) {
      return;
    }

    let missingValues: string[] = [];

    if (Array.isArray(value)) {
      missingValues = value.filter((i) => !options.find((j) => j.value === i));
    } else if (value !== undefined) {
      const valueExists = options.find((i) => i.value === value);
      if (!valueExists) {
        missingValues = [value];
      }
    }
    if (missingValues.length && !calledRef.current) {
      calledRef.current = true;

      const payload: ActionEventSearchPayload = {
        offset: OFFSET_DEFAULT,
        limit: missingValues.length,
        search_conditions: [
          {
            phrases: missingValues.map(String),
            field: ActionEventSearchOptions.EXTERNAL_ID,
            match_type: MatchType.EXACT,
          },
        ],
      };

      queryClient.prefetchQuery({
        queryKey: ACTION_EVENT_QUERY_KEYS.getActionEventSearch(payload),
        queryFn: () => searchActionEvent(payload),
      });

      setNewPayload(payload);
    }
  }, [hasReadEventsPermissions, options, queryClient, value]);

  const onSearch = useCallback((query?: string) => {
    setSearchValue(query || '');

    if (query) {
      setNewPayload({
        offset: OFFSET_DEFAULT,
        limit: INFINITE_SCROLL_LIMIT,
        search_conditions: [
          {
            phrases: [query],
            field: ActionEventSearchOptions.EXTERNAL_ID,
            match_type: MatchType.EXACT,
          },
        ],
      });
    }
  }, []);

  if (!hasReadEventsPermissions) {
    return null;
  }

  let noOptionsText = 'Type to search or paste a list of Action Event IDs';
  if (isV2Error) {
    noOptionsText = 'Search failed. Please try again.';
  } else if (searchValue) {
    noOptionsText = 'No results found';
  }

  const sharedProps: DistributivePick<
    U21SelectSearchProps<ActionEventExternalId, TClearable>,
    | 'loading'
    | 'onExactSearch'
    | 'onSearch'
    | 'options'
    | 'startIcon'
    | 'noOptionsText'
  > = {
    loading: isFetching,
    onExactSearch: async (searchTerms: string[]) => {
      const payload: ActionEventSearchPayload = {
        offset: OFFSET_DEFAULT,
        limit: searchTerms.length,
        search_conditions: [
          {
            phrases: searchTerms.map(String),
            field: ActionEventSearchOptions.EXTERNAL_ID,
            match_type: MatchType.EXACT,
          },
        ],
      };

      await queryClient.prefetchQuery({
        gcTime: Infinity,
        queryKey: ACTION_EVENT_QUERY_KEYS.getActionEventSearch(payload),
        queryFn: () => searchActionEvent(payload),
      });
      refreshActionEvents();
    },
    onSearch,
    options,
    startIcon: <IconSearch />,
    noOptionsText,
  };

  if (multi) {
    const { onChange, ...rest } = props;
    return (
      <U21SelectSearch<ActionEventExternalId, TClearable>
        {...sharedProps}
        {...rest}
        multi
        onChange={(newValues, e) => {
          cachePrefetch(newValues);
          onChange(newValues, e);
        }}
      />
    );
  }
  const { onChange, ...rest } = props;
  return (
    <U21SelectSearch<ActionEventExternalId, TClearable>
      {...sharedProps}
      {...rest}
      onChange={(newValue, e) => {
        if (newValue) {
          cachePrefetch([newValue]);
        }
        onChange(newValue, e);
      }}
    />
  );
};
